import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { Model } from '../../../data/models'
import type { GenerateSettings } from './useGenerateSettings'
import {
	getVideoDuration,
	revokePreviewUrl,
	uploadAndGetUrl,
	type FileCategory,
	type UploadedFile,
	type UploadedVideo,
} from '../utils/uploadHelpers'
import { prepareImageInput } from '../utils/imagePrepare'
import { imageRulesFor } from '../utils/imageInput'
import { showToast } from '../../../components/Toast'
import { getSourceImage } from '../../../utils/sourceImageDb'
import type { InputItem, InputsSnapshot } from '../logic/inputsSnapshot'
import {
	DEFAULT_VIDEO_DURATION,
	MAX_OMNI_REFS,
	MAX_OMNI_REFS_WITH_VIDEO,
} from '../utils/constants'
import {
	DEFAULT_REFERENCE_SECONDS,
	durationFollowsReference,
	formatAllowed,
	formatsLabel,
	isMotionControlModel,
	isVideoReplaceModel,
	motionVideoTooShort,
	replaceVideoTooShort,
	secondsLabel,
	totalSeconds,
} from '../utils/referenceLimits'
import { reportTrimOpened, reportUploadRejected, type RejectField, type RejectReason } from '../../../api/clientEvents'
import { hasFrameRefModes } from '../../../utils/frameRefModes'

const ALLOWED_VIDEO_TYPES = ['video/mp4', 'video/quicktime', 'video/webm']
const ALLOWED_AUDIO_TYPES = [
	'audio/wav',
	'audio/wave',
	'audio/x-wav',
	'audio/mpeg',
	'audio/mp3',
	'audio/aac',
	'audio/mp4',
	'audio/x-m4a',
	'audio/ogg',
]
const MAX_VIDEO_DURATION = 30
/** Потолки размера файлов на сайте, МБ; подсказки полей (UploadsRow) показывают меньший
 *  из этого и лимита провайдера (Model.referenceVideoMaxMb / referenceAudioMaxMb). */
export const UPLOAD_SIZE_MB = { video: 100, audio: 15, image: 10 } as const
const MAX_VIDEO_SIZE_MB = UPLOAD_SIZE_MB.video
const MAX_AUDIO_SIZE_MB = UPLOAD_SIZE_MB.audio
const MAX_IMAGE_SIZE_MB = UPLOAD_SIZE_MB.image

function validateVideoFile(file: File): string | null {
	const type = file.type.toLowerCase()
	const ext = file.name.toLowerCase().split('.').pop() ?? ''
	const ok = type
		? ALLOWED_VIDEO_TYPES.includes(type)
		: ['mp4', 'mov', 'webm'].includes(ext)
	return ok ? null : 'UNSUPPORTED_VIDEO'
}

function validateAudioFile(file: File): string | null {
	const type = file.type.toLowerCase()
	const ext = file.name.toLowerCase().split('.').pop() ?? ''
	const ok = type
		? ALLOWED_AUDIO_TYPES.includes(type)
		: ['wav', 'mp3', 'aac', 'm4a', 'ogg'].includes(ext)
	return ok ? null : 'UNSUPPORTED_AUDIO'
}

function getMaxImages(model: Model | null): number {
	return model?.maxImages ?? 1
}

function getSupportsMultipleImages(model: Model | null): boolean {
	return getMaxImages(model) > 1
}

/** Заглушка пикера «Из моих генераций», пока файл качается (useUploadPickerMenu): пустой файл без ссылки.
 *  У восстановленных после перезагрузки file нет вовсе (только url) — отсюда ?. */
function isPickerPlaceholder(f: UploadedFile | null | undefined): boolean {
	return !!f && !f.url && f.file?.size === 0
}

/** Какие каналы при смене участника мульти-тренда чистить целиком (заперты у новой карточки), см. resetCardRefs. */
export interface ResetCardRefsOptions {
	clearImage?: boolean
	clearVideo?: boolean
	clearAudio?: boolean
}

export function useGenerateUploads(
	selectedModel: Model | null,
	setError: React.Dispatch<React.SetStateAction<string | null>>,
	setSettings: (updater: (prev: GenerateSettings) => GenerateSettings) => void,
	settings?: GenerateSettings,
) {
	const { t } = useTranslation()

	const fileInputRef = useRef<HTMLInputElement>(null)
	const swapInputRef = useRef<HTMLInputElement>(null)
	const videoInputRef = useRef<HTMLInputElement>(null)
	const endImageInputRef = useRef<HTMLInputElement>(null)
	const refImagesInputRef = useRef<HTMLInputElement>(null)
	const refVideoInputRef = useRef<HTMLInputElement>(null)
	const refVideosInputRef = useRef<HTMLInputElement>(null)
	const refAudiosInputRef = useRef<HTMLInputElement>(null)
	const videoDurationRef = useRef<number>(DEFAULT_VIDEO_DURATION)

	const [isUploading, setIsUploading] = useState(false)
	const [isDragging, setIsDragging] = useState(false)
	const [uploadedImage, setUploadedImage] = useState<UploadedFile | null>(null)
	const [referenceImages, setReferenceImages] = useState<UploadedFile[]>([])
	// Именованные фото-слоты карточки (владелец 2026-08-14): позиция = смысл
	// («Ваше фото» / «Очки»), null = слот пуст. referenceImages зеркалит их
	// ПЛОТНЫМ массивом в том же порядке — payload-канал не меняется, промпт
	// карточки командует «первым/вторым фото». Кнопка «Создать» ждёт все слоты.
	const [slotImages, setSlotImages] = useState<(UploadedFile | null)[]>([])
	const slotImagesRef = useRef<(UploadedFile | null)[]>([])
	slotImagesRef.current = slotImages
	// Активный НАБОР номеров слотов (мультистиль: слоты зависят от выбранного
	// стиля, владелец 2026-08-16). null — фильтра нет (обычная карточка).
	// В referenceImages зеркалятся ТОЛЬКО активные слоты — файл слота, который
	// текущий стиль не использует, в payload не утекает.
	const activeSlotNumsRef = useRef<number[] | null>(null)
	// «Слоты в игре» — публичный флаг для UI (например, спрятать переключатель
	// режима Seedance: слоты живут только в режиме «Референсы»).
	const [photoSlotsEngaged, setPhotoSlotsEngaged] = useState(false)
	const [swapImage, setSwapImage] = useState<UploadedFile | null>(null)
	const [uploadedVideo, setUploadedVideo] = useState<UploadedVideo | null>(null)
	const [endImage, setEndImage] = useState<UploadedFile | null>(null)
	const [omniReferenceImages, setOmniReferenceImages] = useState<UploadedFile[]>([])
	const [referenceVideo, setReferenceVideo] = useState<UploadedVideo | null>(null)
	const [referenceVideos, setReferenceVideos] = useState<UploadedVideo[]>([])
	// Эпоха сбросов: растёт при каждом resetUploads. Эффекты «референс под капотом»
	// (GenerateToolMode) зависят от неё и после сброса подставляют админский референс
	// заново — иначе applyCard стирал его через мгновение после подстановки (19.09).
	const [uploadsEpoch, setUploadsEpoch] = useState(0)
	const [referenceAudios, setReferenceAudios] = useState<UploadedFile[]>([])
	const [audioTrimFile, setAudioTrimFile] = useState<File | null>(null)
	// Тример видео-референса: ссылка уже загруженного ролика, который длиннее лимита модели.
	const [videoTrimUrl, setVideoTrimUrl] = useState<string | null>(null)
	const [selectedVideoDuration, setSelectedVideoDuration] = useState<number>(DEFAULT_VIDEO_DURATION)

	useEffect(() => {
		if (referenceAudios.length > 0) {
			setSettings((prev) => (prev.sound ? prev : { ...prev, sound: true }))
		}
	}, [referenceAudios.length, setSettings])

	// Рамки фото и секунды референсов — из кода адаптера (см. utils/referenceLimits, utils/imageInput).
	const imageRules = useMemo(() => imageRulesFor(selectedModel), [selectedModel])
	const audioMaxSeconds = selectedModel?.maxReferenceAudioSeconds ?? DEFAULT_REFERENCE_SECONDS
	// Видео: проверяем длину только когда модель назвала лимит; чужих чисел не выдумываем.
	const videoMaxSeconds: number | null = selectedModel?.maxReferenceVideoSeconds ?? null
	const videoFormats = selectedModel?.referenceVideoFormats
	const audioFormats = selectedModel?.referenceAudioFormats
	const videoMaxMb = Math.min(MAX_VIDEO_SIZE_MB, selectedModel?.referenceVideoMaxMb || MAX_VIDEO_SIZE_MB)
	const audioMaxMb = Math.min(MAX_AUDIO_SIZE_MB, selectedModel?.referenceAudioMaxMb || MAX_AUDIO_SIZE_MB)
	const modelName = selectedModel?.name || 'Модель'
	const modelKey = selectedModel?.backendModel || selectedModel?.id
	// Отказ до запроса — в учёт (api/clientEvents): сервер таких ошибок иначе не видит.
	const track = useCallback(
		(field: RejectField, reason: RejectReason, detail?: string) => reportUploadRejected({ model: modelKey, field, reason, detail }),
		[modelKey],
	)

	// Seedance 2.5 с одним видео-референсом делает ролик длиной референса — пилюля
	// длительности и цена следуют за ним (utils/referenceLimits). Вызывается из
	// обработчиков событий, не из эффекта (конвенция репо).
	const syncDurationToReference = useCallback(
		(videos: UploadedVideo[]) => {
			const follow = durationFollowsReference(selectedModel, settings ?? {}, videos)
			if (follow == null) return
			setSettings((prev) => (prev.duration === String(follow) ? prev : { ...prev, duration: String(follow) }))
		},
		[selectedModel, settings, setSettings],
	)
	const requiresImage = selectedModel?.requiresImage ?? false
	const requiresTwoImages = selectedModel?.requiresTwoImages ?? false
	const supportsMultipleImages = getSupportsMultipleImages(selectedModel)
	const maxImages = getMaxImages(selectedModel)

	const resolveValidationError = useCallback(
		(code: string | null): string | null => {
			if (!code) return null
			if (code === 'UNSUPPORTED_VIDEO') return t('generate.upload.error.unsupportedVideo')
			if (code === 'UNSUPPORTED_AUDIO') return 'Неподдерживаемый формат аудио. Используйте wav, mp3, aac, m4a или ogg.'
			return code
		},
		[t],
	)

	// Фото на вход — один обработчик для всех каналов (utils/imagePrepare, владелец 29.09): подходящее уходит
	// как есть, упаковку и размер чиним сами, не чинится — понятный текст до загрузки. Отмена окна рамки —
	// молча. Что сделали — уведомлением (на телефоне пометку у превью не навести) и пометкой у превью.
	// Свой прошлый отказ снимает новая попытка загрузки (clearOwnReject в начале обработчика; у пачки — один раз
	// до цикла, иначе подходящий файл стёр бы отказ соседа по пачке). Снимаем, только если на плашке всё ещё наш
	// текст: ошибки генерации и прочие не трогаем.
	const lastRejectRef = useRef<string | null>(null)
	const clearOwnReject = useCallback(() => {
		const shown = lastRejectRef.current
		if (!shown) return
		lastRejectRef.current = null
		setError((cur) => (cur === shown ? null : cur))
	}, [setError])
	const prepareImage = useCallback(
		async (raw: File, field: RejectField): Promise<{ file: File; note?: string } | null> => {
			// Пока фото читается и подгоняется (у крупного это заметно, у вытянутого ждёт окно рамки), слот показывает
			// загрузку — иначе он пуст и тянет нажать ещё раз. Удача — загрузку дальше ведёт обработчик, отказ — снимаем.
			setIsUploading(true)
			const res = await prepareImageInput(raw, imageRules, { maxMb: MAX_IMAGE_SIZE_MB })
			if (!res.ok) {
				setIsUploading(false)
				if (res.cancelled) return null
				track(field, res.reason, res.reason === 'dimensions' ? res.message.slice(0, 80) : raw.name.split('.').pop())
				setError(res.message)
				lastRejectRef.current = res.message
				return null
			}
			if (res.note) showToast(res.note, 'info')
			return { file: res.file, note: res.note }
		},
		[imageRules, track, setError],
	)

	// Атомарное обновление слотов + зеркала referenceImages (без эффекта-синка:
	// ref всегда свежий, оба set в одном тике — payload не разъезжается).
	const mirrorSlots = useCallback((slots: (UploadedFile | null)[]) => {
		const nums = activeSlotNumsRef.current
		const mirrored = slots.filter((f, i): f is UploadedFile => !!f && (nums == null || nums.includes(i + 1)))
		// Фото сцены (персонаж, локация, предмет) лежат вслед за слотами карточки — зеркало их сохраняет. Иначе
		// загрузка фото в слот стирала их, и сцена считала, что юзер сам убрал ассет: чип исчезал из промпта.
		setReferenceImages((prev) => {
			const scene = prev.filter((f) => f.sceneAsset)
			return scene.length ? [...mirrored, ...scene] : mirrored
		})
	}, [])

	const applySlotImages = useCallback(
		(mutate: (prev: (UploadedFile | null)[]) => (UploadedFile | null)[]) => {
			const next = mutate(slotImagesRef.current)
			slotImagesRef.current = next
			setSlotImages(next)
			mirrorSlots(next)
		},
		[mirrorSlots],
	)

	/** Сменить активный набор слотов (UploadsRow, при смене стиля) —
	 *  зеркало referenceImages пересобирается под новый набор. */
	const setActiveSlotNums = useCallback(
		(nums: number[] | null) => {
			const prev = activeSlotNumsRef.current
			const same =
				prev === nums ||
				(prev != null && nums != null && prev.length === nums.length && prev.every((n, i) => n === nums[i]))
			if (same) return
			activeSlotNumsRef.current = nums
			setPhotoSlotsEngaged(nums != null && nums.length > 0)
			// Слоты вообще не в игре — referenceImages не трогаем (обычные
			// мульти-загрузки живут своей жизнью).
			if (slotImagesRef.current.length) mirrorSlots(slotImagesRef.current)
		},
		[mirrorSlots],
	)

	const handleSlotImageUpload = useCallback(
		async (idx: number, e: React.ChangeEvent<HTMLInputElement> | { target: { files: File[] } }) => {
			const raw = e.target.files?.[0]
			const resetInput = () => {
				if ('value' in e.target) (e.target as HTMLInputElement).value = ''
			}
			if (!raw) return
			clearOwnReject()
			const prep = await prepareImage(raw, 'reference_images')
			if (!prep) {
				// Заглушка пикера «Из моих генераций» (пустой файл без ссылки) не должна зависнуть в слоте.
				applySlotImages((prev) => prev.map((f, i) => (i === idx && isPickerPlaceholder(f) ? null : f)))
				resetInput()
				return
			}
			const { file, note } = prep
			const preview = URL.createObjectURL(file)
			applySlotImages((prev) => {
				const next = [...prev]
				while (next.length <= idx) next.push(null)
				const old = next[idx]
				if (old) revokePreviewUrl(old.preview)
				next[idx] = { file, preview, note }
				return next
			})
			setIsUploading(true)
			try {
				const url = await uploadAndGetUrl(file, 'images')
				applySlotImages((prev) =>
					prev.map((f, i) => (i === idx && f && f.preview === preview ? { ...f, url } : f)),
				)
			} catch (err) {
				setError(err instanceof Error ? err.message : t('generate.upload.error.uploadFailed'))
				applySlotImages((prev) => prev.map((f, i) => (i === idx && f && f.preview === preview ? null : f)))
			} finally {
				setIsUploading(false)
				resetInput()
			}
		},
		[applySlotImages, clearOwnReject, prepareImage, setError, t],
	)

	/** Плейсхолдер-прелоадер в слоте, пока файл из «Моих генераций» скачивается (пикер источника, 25.09):
	 *  preview — превью из галереи (может быть пустым), null — убрать. Реальный файл кладёт handleSlotImageUpload. */
	const setSlotImagePending = useCallback(
		(idx: number, preview: string | null) => {
			applySlotImages((prev) => {
				const next = [...prev]
				while (next.length <= idx) next.push(null)
				next[idx] = preview == null ? null : { file: new File([], ''), preview }
				return next
			})
		},
		[applySlotImages],
	)

	const removeSlotImage = useCallback(
		(idx: number) => {
			applySlotImages((prev) =>
				prev.map((f, i) => {
					if (i !== idx) return f
					if (f) revokePreviewUrl(f.preview)
					return null
				}),
			)
		},
		[applySlotImages],
	)

	// Seedance 2 «Кадры | Референсы» (владелец 23.10): у режимов разные каналы, и фото, попавшее не в свой канал
	// (восстановление снимка загрузок после перехода, сцена, смена модели), виджет не рисует — видны две пустые иконки,
	// а в запрос оно ушло бы молча первым кадром (getImageInput). Переносим в канал текущего режима: «Кадры» — первые
	// два фото в кадры, лишнее снимаем (кадров два); «Референсы» — кадры в ряд референсов.
	const seedanceFramesNow = hasFrameRefModes(selectedModel) && (settings?.seedanceMode ?? 'frames') === 'frames'
	const seedanceRefsNow = hasFrameRefModes(selectedModel) && settings?.seedanceMode === 'references'
	useEffect(() => {
		if (seedanceFramesNow && referenceImages.length > 0) {
			const [first, second] = referenceImages
			setUploadedImage((cur) => cur ?? first)
			if (second) setEndImage((cur) => cur ?? second)
			setReferenceImages(() => [])
		} else if (seedanceRefsNow && (uploadedImage || endImage)) {
			const moved = [uploadedImage, endImage].filter((f): f is UploadedFile => !!f)
			setReferenceImages((prev) => [...moved.filter((m) => !prev.some((pf) => pf.preview === m.preview)), ...prev].slice(0, maxImages))
			setUploadedImage(null)
			setEndImage(null)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [seedanceFramesNow, seedanceRefsNow, referenceImages, uploadedImage, endImage])

	/** Сбросить только референсы карточки «под капотом» (cardRef: фото/видео/аудио из админского пресета), фото и
	 *  видео пользователя оставить. Переключение участника мульти-тренда с тем же числом полей (23.10): одно селфи
	 *  примеряется на все образы, а референсы новой карточки подставляются заново по эпохе. */
	const resetCardRefs = useCallback((opts?: ResetCardRefsOptions) => {
		setUploadsEpoch((n) => n + 1)
		// all — канал заперт у новой карточки (её референс вместо фото юзера, слот скрыт): чистим целиком, иначе своё
		// фото/видео уехало бы в модель невидимым, а запертый референс не подставился бы (канал занят).
		const dropOne = <T extends { cardRef?: boolean; preview: string }>(all: boolean) => (cur: T | null): T | null => {
			if (cur && (all || cur.cardRef)) { revokePreviewUrl(cur.preview); return null }
			return cur
		}
		const dropMany = <T extends { cardRef?: boolean; preview: string }>(all: boolean) => (prev: T[]): T[] => {
			if (!prev.some((f) => all || f.cardRef)) return prev
			prev.forEach((f) => { if (all || f.cardRef) revokePreviewUrl(f.preview) })
			return prev.filter((f) => !(all || f.cardRef))
		}
		setUploadedImage(dropOne(!!opts?.clearImage))
		// Фото юзера в мульти-входе остаются и при замке: сцена карточки встанет первой (handleImageUpload, cardRef).
		setReferenceImages(dropMany(false))
		setUploadedVideo(dropOne(!!opts?.clearVideo))
		setReferenceVideo(dropOne(!!opts?.clearVideo))
		setReferenceVideos(dropMany(!!opts?.clearVideo))
		setReferenceAudios(dropMany(!!opts?.clearAudio))
		if (opts?.clearVideo) setSelectedVideoDuration(DEFAULT_VIDEO_DURATION)
	}, [])

	const resetUploads = useCallback(() => {
		setUploadsEpoch((n) => n + 1)
		;[uploadedImage, swapImage, endImage].forEach((f) => f && revokePreviewUrl(f.preview))
		referenceImages.forEach((f) => revokePreviewUrl(f.preview))
		omniReferenceImages.forEach((f) => revokePreviewUrl(f.preview))
		;[uploadedVideo, referenceVideo].forEach((f) => f && revokePreviewUrl(f.preview))
		referenceVideos.forEach((f) => revokePreviewUrl(f.preview))
		referenceAudios.forEach((f) => revokePreviewUrl(f.preview))

		setUploadedImage(null)
		setReferenceImages([])
		slotImagesRef.current = []
		activeSlotNumsRef.current = null
		setSlotImages([])
		setSwapImage(null)
		setUploadedVideo(null)
		setEndImage(null)
		setOmniReferenceImages([])
		setReferenceVideo(null)
		setReferenceVideos([])
		setReferenceAudios([])
		setSelectedVideoDuration(DEFAULT_VIDEO_DURATION)
	}, [
		uploadedImage,
		swapImage,
		endImage,
		referenceImages,
		omniReferenceImages,
		uploadedVideo,
		referenceVideo,
		referenceVideos,
		referenceAudios,
	])

	/**
	 * Вернуть входы из снимка стикера — каждый канал СВОИМ сеттером, в то место,
	 * где он лежал на старте (см. inputsSnapshot.ts). Не через handleImageUpload:
	 * тот раскладывает по флагам ТЕКУЩЕЙ модели и не знает именованных слотов —
	 * так фото видео-трендов и терялось при возврате (владелец 22.09).
	 *
	 * Файл берём из IndexedDB (превью с blob не протухает), ссылку — из снимка;
	 * повторной загрузки нет — в запрос уходит `url`, бэк пересигнивает. Если
	 * ссылки нет, а файл есть (редкий обрыв загрузки на старте) — грузим заново.
	 */
	const restoreInputs = useCallback(
		async (snap: InputsSnapshot) => {
			const toItem = async (
				it: InputItem | null | undefined,
				kind: FileCategory,
			): Promise<UploadedVideo | null> => {
				if (!it) return null
				let file: File | null = null
				if (it.dbKey) {
					try {
						file = await getSourceImage(it.dbKey)
					} catch {
						file = null
					}
				}
				let url = it.url
				if (!url && file) {
					try {
						url = await uploadAndGetUrl(file, kind)
					} catch {
						return null
					}
				}
				if (!url && !file) return null
				const name = it.name || url?.split('?', 1)[0].split('/').pop() || 'input'
				const out: UploadedVideo = {
					file: file ?? new File([], name, { type: it.type || '' }),
					preview: file ? URL.createObjectURL(file) : (url as string),
					url,
				}
				if (it.cardRef) out.cardRef = true
				if (typeof it.duration === 'number') out.duration = it.duration
				return out
			}
			const toList = async (items: InputItem[] | undefined, kind: FileCategory) => {
				const out: UploadedVideo[] = []
				for (const it of items ?? []) {
					const v = await toItem(it, kind)
					if (v) out.push(v)
				}
				return out
			}

			setIsUploading(true)
			try {
				const [image, swap, end, video, refVideo, omni, refVideos, refAudios, refImages] =
					await Promise.all([
						toItem(snap.image, 'images'),
						toItem(snap.swap, 'images'),
						toItem(snap.end, 'images'),
						toItem(snap.video, 'videos'),
						toItem(snap.refVideo, 'videos'),
						toList(snap.omniRefImages, 'images'),
						toList(snap.refVideos, 'videos'),
						toList(snap.refAudios, 'audio'),
						toList(snap.refImages, 'images'),
					])
				setUploadedImage(image)
				setSwapImage(swap)
				setEndImage(end)
				setUploadedVideo(video)
				setReferenceVideo(refVideo)
				setOmniReferenceImages(omni)
				setReferenceVideos(refVideos)
				setReferenceAudios(refAudios)
				if (snap.slots) {
					// Слоты — через applySlotImages: он же зеркалит их в referenceImages
					// (payload собирается из зеркала, UI рисует слоты).
					const slots = await Promise.all(snap.slots.map((it) => toItem(it, 'images')))
					applySlotImages(() => slots)
				} else {
					setReferenceImages(refImages)
				}
				if (typeof snap.videoDuration === 'number') setSelectedVideoDuration(snap.videoDuration)
			} finally {
				setIsUploading(false)
			}
		},
		[applySlotImages],
	)

	const handleImageUpload = useCallback(
		// opts.cardRef — фото подставил админский пресет карточки (applyDefaultUploadedImage): помечаем, чтобы при
		// смене участника мульти-тренда сбросить его, а фото пользователя оставить (resetCardRefs).
		async (e: React.ChangeEvent<HTMLInputElement> | { target: { files: File[] } }, opts?: { cardRef?: boolean }) => {
			const raw = e.target.files?.[0]
			if (!raw) return
			if ('value' in e.target) (e.target as HTMLInputElement).value = ''
			clearOwnReject()
			// Режим по умолчанию — «Кадры» (как в пилюле и в payload): без записи в настройках фото всё равно идёт в кадр.
			const isSeedance2Frames = hasFrameRefModes(selectedModel) && (settings?.seedanceMode ?? 'frames') === 'frames'
			const toReferences = supportsMultipleImages && !requiresImage && !requiresTwoImages && !isSeedance2Frames
			// Места нет — говорим сразу, до подгонки: не заставлять выбирать рамку ради отказа.
			if (toReferences && referenceImages.length >= maxImages) {
				track('reference_images', 'too_many', `${maxImages}`)
				setError(t('generate.upload.error.maxImages', { max: maxImages }))
				return
			}
			// Seedance отклонял фото вне 300–6000 px и вытянутее 2,5:1 уже после списания — подгоняем до загрузки.
			const prep = await prepareImage(raw, 'image')
			if (!prep) {
				setUploadedImage((cur) => (isPickerPlaceholder(cur) ? null : cur))
				return
			}
			const { file, note } = prep
			const preview = URL.createObjectURL(file)

			if (toReferences) {
				if (referenceImages.length >= maxImages) {
					track('reference_images', 'too_many', `${maxImages}`)
					setError(t('generate.upload.error.maxImages', { max: maxImages }))
					revokePreviewUrl(preview)
					setIsUploading(false)
					return
				}
				// Референс карточки — ПЕРВЫМ: админ задаёт сцену, фото юзера после (при keepUploads они уже лежат в списке).
				const entry = { file, preview, note, cardRef: opts?.cardRef || undefined }
				setReferenceImages((prev) => (opts?.cardRef ? [entry, ...prev] : [...prev, entry]))
				setIsUploading(true)
				try {
					const url = await uploadAndGetUrl(file, 'images')
					setReferenceImages((prev) =>
						prev.map((img) => (img.preview === preview ? { ...img, url } : img)),
					)
				} catch (err) {
					setError(err instanceof Error ? err.message : t('generate.upload.error.uploadFailed'))
					setReferenceImages((prev) => prev.filter((img) => img.preview !== preview))
					revokePreviewUrl(preview)
				} finally {
					setIsUploading(false)
				}
			} else {
				const cardRef = opts?.cardRef || undefined
				setUploadedImage({ file, preview, note, cardRef })
				setIsUploading(true)
				try {
					const url = await uploadAndGetUrl(file, 'images')
					setUploadedImage({ file, preview, url, note, cardRef })
				} catch (err) {
					setError(err instanceof Error ? err.message : t('generate.upload.error.uploadFailed'))
					setUploadedImage(null)
					revokePreviewUrl(preview)
				} finally {
					setIsUploading(false)
				}
			}
		},
		[
			clearOwnReject,
			prepareImage,
			track,
			supportsMultipleImages,
			requiresImage,
			requiresTwoImages,
			referenceImages,
			maxImages,
			setError,
			t,
			selectedModel,
			settings?.seedanceMode,
		],
	)

	const handleMultipleImageUpload = useCallback(
		async (e: React.ChangeEvent<HTMLInputElement>) => {
			const files = Array.from(e.target.files || [])
			if (e.target) e.target.value = ''
			if (!files.length) return
			clearOwnReject()
			const remaining = maxImages - referenceImages.length
			if (remaining <= 0) {
				track('reference_images', 'too_many', `${maxImages}`)
				setError(t('generate.upload.error.maxImages', { max: maxImages }))
				return
			}
			const filesToUpload = files.slice(0, remaining)
			// По одному: окно рамки у вытянутого фото и отказ одного файла не теряют остальные (раньше один
			// неподходящий файл отменял всю пачку).
			const newImages: UploadedFile[] = []
			for (const raw of filesToUpload) {
				const prep = await prepareImage(raw, 'reference_images')
				if (prep) newImages.push({ file: prep.file, preview: URL.createObjectURL(prep.file), note: prep.note })
			}
			// «Добавлено X из Y» — только если своих отказов в пачке не было: текст отказа важнее.
			if (files.length > remaining && newImages.length === filesToUpload.length) {
				setError(t('generate.upload.error.partialUpload', { added: filesToUpload.length, total: files.length, max: maxImages }))
			}
			if (!newImages.length) return
			setReferenceImages((prev) => [...prev, ...newImages])
			setIsUploading(true)
			try {
				for (const img of newImages) {
					const url = await uploadAndGetUrl(img.file, 'images')
					setReferenceImages((prev) =>
						prev.map((i) => (i.preview === img.preview ? { ...i, url } : i)),
					)
				}
			} catch (err) {
				setError(err instanceof Error ? err.message : t('generate.upload.error.uploadFailed'))
			} finally {
				setIsUploading(false)
			}
		},
		[maxImages, clearOwnReject, prepareImage, track, referenceImages.length, setError, t],
	)

	const handleSwapImageUpload = useCallback(
		async (e: React.ChangeEvent<HTMLInputElement> | { target: { files: File[] } }) => {
			const raw = e.target.files?.[0]
			if (!raw) return
			clearOwnReject()
			const prep = await prepareImage(raw, 'image')
			if ('value' in e.target) (e.target as HTMLInputElement).value = ''
			if (!prep) {
				setSwapImage((cur) => (isPickerPlaceholder(cur) ? null : cur))
				return
			}
			const { file, note } = prep
			const preview = URL.createObjectURL(file)
			setSwapImage({ file, preview, note })
			setIsUploading(true)
			try {
				const url = await uploadAndGetUrl(file, 'images')
				setSwapImage({ file, preview, url, note })
			} catch (err) {
				setError(err instanceof Error ? err.message : t('generate.upload.error.uploadFailed'))
				setSwapImage(null)
				revokePreviewUrl(preview)
			} finally {
				setIsUploading(false)
			}
		},
		[clearOwnReject, prepareImage, setError, t],
	)

	const handleVideoUpload = useCallback(
		async (e: React.ChangeEvent<HTMLInputElement> | { target: { files: File[] } }) => {
			const file = e.target.files?.[0]
			if (!file) return
			const formatError = resolveValidationError(validateVideoFile(file))
			if (formatError) { setError(formatError); return }
			const preview = URL.createObjectURL(file)
			setUploadedVideo({ file, preview })
			setIsUploading(true)
			const exact = await getVideoDuration(file)
			// Округление как у сервера (utils/video_trim): с Math.ceil 7,2 с становились 8 на ползунке, и сервер отвечал
			// «Длительность ограничена длиной видео-референса (7 сек)» на ролик, который клиент не удлинял (30.09).
			const duration = Math.round(exact)

			const tooShort = isMotionControlModel(selectedModel)
				? motionVideoTooShort(exact)
				: isVideoReplaceModel(selectedModel) ? replaceVideoTooShort(exact) : null
			if (tooShort) {
				setError(tooShort)
				track('video', 'too_short', `${exact.toFixed(1)}s`)
				setUploadedVideo(null)
				revokePreviewUrl(preview)
				setIsUploading(false)
				return
			}

			if (file.size > MAX_VIDEO_SIZE_MB * 1024 * 1024) {
				setError(`Видео превышает ${MAX_VIDEO_SIZE_MB} МБ. Пожалуйста, сократите файл перед загрузкой.`)
				setUploadedVideo(null)
				revokePreviewUrl(preview)
				setIsUploading(false)
				return
			}

			if (duration > MAX_VIDEO_DURATION) {
				setError(`Видео длиннее ${MAX_VIDEO_DURATION} секунд. Загрузите более короткое видео или обрежьте его.`)
				setUploadedVideo(null)
				revokePreviewUrl(preview)
				setIsUploading(false)
				return
			}

			videoDurationRef.current = duration
			setSelectedVideoDuration(duration)
			setUploadedVideo({ file, preview, duration })
			if (duration > 10) {
				setSettings((prev) => ({ ...prev, characterOrientation: 'video' as const }))
			}
			try {
				const url = await uploadAndGetUrl(file, 'videos')
				setUploadedVideo({ file, preview, url, duration: videoDurationRef.current })
			} catch (err) {
				setError(err instanceof Error ? err.message : t('generate.upload.error.uploadFailed'))
				setUploadedVideo(null)
				revokePreviewUrl(preview)
			} finally {
				setIsUploading(false)
			}
		},
		[setError, setSettings, resolveValidationError, t, selectedModel, track],
	)

	const handleEndImageUpload = useCallback(
		async (e: React.ChangeEvent<HTMLInputElement> | { target: { files: File[] } }) => {
			const raw = e.target.files?.[0]
			if (!raw) return
			// Последний кадр Seedance (last_frame_url) — те же рамки, что у первого кадра.
			clearOwnReject()
			const prep = await prepareImage(raw, 'image')
			if ('value' in e.target) (e.target as HTMLInputElement).value = ''
			if (!prep) {
				setEndImage((cur) => (isPickerPlaceholder(cur) ? null : cur))
				return
			}
			const { file, note } = prep
			const preview = URL.createObjectURL(file)
			setEndImage({ file, preview, note })
			setIsUploading(true)
			try {
				const url = await uploadAndGetUrl(file, 'images')
				setEndImage({ file, preview, url, note })
			} catch (err) {
				setError(err instanceof Error ? err.message : t('generate.upload.error.uploadError'))
				setEndImage(null)
				revokePreviewUrl(preview)
			} finally {
				setIsUploading(false)
			}
		},
		[clearOwnReject, prepareImage, setError, t],
	)

	const handleOmniRefImageUpload = useCallback(
		async (e: React.ChangeEvent<HTMLInputElement>) => {
			const files = Array.from(e.target.files || [])
			if (e.target) e.target.value = ''
			if (!files.length) return
			clearOwnReject()
			const maxRef = referenceVideo ? MAX_OMNI_REFS_WITH_VIDEO : MAX_OMNI_REFS
			const remaining = maxRef - omniReferenceImages.length
			if (remaining <= 0) {
				setError(t('generate.upload.error.maxRefs', { max: maxRef }))
				return
			}
			const newImgs: UploadedFile[] = []
			for (const raw of files.slice(0, remaining)) {
				const prep = await prepareImage(raw, 'reference_images')
				if (prep) newImgs.push({ file: prep.file, preview: URL.createObjectURL(prep.file), note: prep.note })
			}
			if (!newImgs.length) return
			setOmniReferenceImages((prev) => [...prev, ...newImgs])
			setIsUploading(true)
			try {
				for (const img of newImgs) {
					const url = await uploadAndGetUrl(img.file, 'images')
					setOmniReferenceImages((prev) =>
						prev.map((i) => (i.preview === img.preview ? { ...i, url } : i)),
					)
				}
			} catch (err) {
				setError(err instanceof Error ? err.message : t('generate.upload.error.uploadError'))
			} finally {
				setIsUploading(false)
			}
		},
		[referenceVideo, omniReferenceImages.length, clearOwnReject, prepareImage, setError, t],
	)

	const handleReferenceVideoUpload = useCallback(
		async (e: React.ChangeEvent<HTMLInputElement> | { target: { files: File[] } }) => {
			const file = e.target.files?.[0]
			if (!file) return
			const formatError = resolveValidationError(validateVideoFile(file))
			if (formatError) { setError(formatError); return }
			const preview = URL.createObjectURL(file)
			const duration = Math.ceil(await getVideoDuration(file))
			setReferenceVideo({ file, preview, duration })
			setSettings((prev) => ({ ...prev, sound: false }))
			setIsUploading(true)
			try {
				const url = await uploadAndGetUrl(file, 'videos')
				setReferenceVideo({ file, preview, url, duration })
			} catch (err) {
				setError(err instanceof Error ? err.message : t('generate.upload.error.uploadError'))
				setReferenceVideo(null)
				revokePreviewUrl(preview)
			} finally {
				setIsUploading(false)
			}
		},
		[setError, setSettings, resolveValidationError, t],
	)

	const applyDefaultReferenceVideo = useCallback(
		async (url: string) => {
			try {
				const filename = url.split('/').pop() || 'reference.mp4'
				const file = new File([], filename, { type: 'video/mp4' })
				const preview = url
				const duration = DEFAULT_VIDEO_DURATION
				setReferenceVideo({ file, preview, url, duration, cardRef: true })
				setSettings((prev) => ({ ...prev, sound: false }))
			} catch {
				// ignore
			}
		},
		[setReferenceVideo, setSettings],
	)

	/** Мульти-канал видео-референсов (модели с maxReferenceVideos > 1):
	 *  референс «под капотом» из админ-карточки — лёгкая URL-обёртка без
	 *  скачивания (payload шлёт .url → video_urls). Занятый канал не трогаем. */
	const applyDefaultReferenceVideos = useCallback(
		(url: string) => {
			const filename = url.split('/').pop() || 'reference.mp4'
			const file = new File([], filename, { type: 'video/mp4' })
			setReferenceVideos((prev) => (prev.length ? prev : [{ file, preview: url, url, cardRef: true }]))
		},
		[setReferenceVideos],
	)

	/** Аудио-референс «под капотом» из админ-карточки → referenceAudios
	 *  (payload шлёт .url → audio_urls). Занятый канал не трогаем. */
	const applyDefaultReferenceAudio = useCallback(
		(url: string) => {
			const filename = url.split('/').pop() || 'reference.mp3'
			const file = new File([], filename, { type: 'audio/mpeg' })
			setReferenceAudios((prev) => (prev.length ? prev : [{ file, preview: url, url, cardRef: true }]))
		},
		[setReferenceAudios],
	)

	const applyDefaultUploadedVideo = useCallback(
		async (url: string) => {
			try {
				const filename = url.split('/').pop() || 'reference.mp4'
				const file = new File([], filename, { type: 'video/mp4' })
				const preview = url
				setUploadedVideo({ file, preview, url, cardRef: true })
			} catch {
				// ignore
			}
		},
		[setUploadedVideo],
	)

	/**
	 * Подставляет дефолтное изображение из стиля в слот uploadedImage (слот 1).
	 * Используется для моделей с requiresImage=true, когда стиль имеет defaultMediaUrl с изображением.
	 */
	const applyDefaultUploadedImage = useCallback(
		async (url: string) => {
			try {
				const response = await fetch(url)
				if (!response.ok) throw new Error(`HTTP ${response.status}`)
				const blob = await response.blob()
				// Имя берём БЕЗ query: presigned-ссылки (студия «в работу», S3)
				// несут подпись на сотни символов — такое имя в multipart роняло
				// весь upload («Failed to fetch»), а расширение переставало
				// определяться («webp?X-Amz-…» мимо mimeMap).
				const filename = url.split('?', 1)[0].split('/').pop() || 'reference.jpg'
				const ext = filename.split('.').pop()?.toLowerCase() || 'jpg'
				const mimeMap: Record<string, string> = {
					jpg: 'image/jpeg', jpeg: 'image/jpeg',
					png: 'image/png', webp: 'image/webp', gif: 'image/gif',
				}
				const type = mimeMap[ext] || blob.type || 'image/jpeg'
				const file = new File([blob], filename, { type })
				// Используем handleImageUpload для полноценной загрузки (upload на S3 + preview); cardRef — это референс
				// карточки, не фото пользователя.
				await handleImageUpload({ target: { files: [file] } }, { cardRef: true })
			} catch {
				// ignore
			}
		},
		[handleImageUpload],
	)

	const handleReferenceVideosUpload = useCallback(
		async (e: React.ChangeEvent<HTMLInputElement>) => {
			const files = Array.from(e.target.files || [])
			if (!files.length) return
			const reset = () => { if (e.target) e.target.value = '' }
			const invalidFile = files.find((f) => validateVideoFile(f) !== null)
			if (invalidFile) {
				track('reference_videos', 'format', invalidFile.name.split('.').pop())
				setError(t('generate.upload.error.unsupportedVideo'))
				reset()
				return
			}
			// Форматы провайдера: Seedance читает только mp4 и mov — webm из браузера
			// отклоняется уже после списания. Говорим сразу и называем допустимые.
			const wrongFormat = files.find((f) => !formatAllowed(f, videoFormats))
			if (wrongFormat && videoFormats?.length) {
				track('reference_videos', 'format', wrongFormat.name.split('.').pop())
				setError(`${modelName} принимает видео только в ${formatsLabel(videoFormats)}. Файл «${wrongFormat.name}» не подойдёт.`)
				reset()
				return
			}
			const maxRefs = selectedModel?.maxReferenceVideos ?? 3
			const remaining = maxRefs - referenceVideos.length
			if (remaining <= 0) {
				track('reference_videos', 'too_many', `${maxRefs}`)
				setError(`Максимум ${maxRefs} видео.`)
				reset()
				return
			}
			const tooBig = files.find((f) => f.size > videoMaxMb * 1024 * 1024)
			if (tooBig) {
				track('reference_videos', 'too_big', `${Math.round(tooBig.size / 1048576)} МБ`)
				setError(`Видео превышает ${videoMaxMb} МБ.`)
				reset()
				return
			}
			const filesToUpload = files.slice(0, remaining)
			const newVids: UploadedVideo[] = []
			// Слишком короткий референс не удлинить — не пускаем. Слишком длинный НЕ режем
			// сами: ролик уходит в тример, и отрезок выбирает пользователь (25.09).
			const minSeconds = selectedModel?.minReferenceVideoSeconds
			for (const f of filesToUpload) {
				const preview = URL.createObjectURL(f)
				const duration = await getVideoDuration(f)
				if (minSeconds && duration > 0 && duration < minSeconds) {
					track('reference_videos', 'too_short', `${secondsLabel(duration)} с`)
					revokePreviewUrl(preview)
					newVids.forEach((v) => revokePreviewUrl(v.preview))
					setError(`Видео-референс короче ${minSeconds} с (ваше: ${secondsLabel(duration)} с). Модель принимает только более длинные.`)
					reset()
					return
				}
				newVids.push({ file: f, preview, duration })
			}
			const longOnes = videoMaxSeconds == null ? [] : newVids.filter((v) => (v.duration || 0) > videoMaxSeconds + 0.25)
			if (videoMaxSeconds != null && longOnes.length) {
				if (newVids.length > 1) {
					track('reference_videos', 'too_long', `${Math.round(longOnes[0].duration || 0)} с, несколько файлов`)
					newVids.forEach((v) => revokePreviewUrl(v.preview))
					setError(`Ролик «${longOnes[0].file.name}» длиннее ${videoMaxSeconds} с. Загрузите его отдельно, чтобы выбрать отрезок.`)
					reset()
					return
				}
				// Один длинный файл: заливаем и открываем тример — в список он попадёт уже отрезком.
				const v = newVids[0]
				reportTrimOpened({ model: modelKey, field: 'reference_videos', detail: `${Math.round(v.duration || 0)} с` })
				setIsUploading(true)
				try {
					const url = await uploadAndGetUrl(v.file, 'videos')
					setVideoTrimUrl(url)
				} catch (err) {
					setError(err instanceof Error ? err.message : t('generate.upload.error.uploadError'))
				} finally {
					revokePreviewUrl(v.preview)
					setIsUploading(false)
					reset()
				}
				return
			}
			if (videoMaxSeconds != null && totalSeconds(referenceVideos) + totalSeconds(newVids) > videoMaxSeconds + 0.25) {
				track('reference_videos', 'total_seconds', `${Math.round(totalSeconds(referenceVideos) + totalSeconds(newVids))} с`)
				newVids.forEach((v) => revokePreviewUrl(v.preview))
				setError(`Все видео-референсы вместе не длиннее ${videoMaxSeconds} с. Уберите ролик или загрузите короче.`)
				reset()
				return
			}
			const nextVideos = [...referenceVideos, ...newVids]
			setReferenceVideos(nextVideos)
			syncDurationToReference(nextVideos)
			setIsUploading(true)
			try {
				for (const v of newVids) {
					const url = await uploadAndGetUrl(v.file, 'videos')
					setReferenceVideos((prev) =>
						prev.map((i) => (i.preview === v.preview ? { ...i, url } : i)),
					)
				}
			} catch (err) {
				setError(err instanceof Error ? err.message : t('generate.upload.error.uploadError'))
			} finally {
				setIsUploading(false)
				reset()
			}
		},
		[selectedModel, referenceVideos, videoFormats, videoMaxMb, videoMaxSeconds, modelName, modelKey, track, syncDurationToReference, setError, t],
	)

	/** Отрезок, выбранный в тримере: в список референсов и пилюлю длительности. */
	const applyTrimmedVideo = useCallback(
		(trimmedUrl: string, duration: number) => {
			setVideoTrimUrl(null)
			const entry: UploadedVideo = { file: new File([], 'trimmed.mp4', { type: 'video/mp4' }), preview: trimmedUrl, url: trimmedUrl, duration }
			const nextVideos = [...referenceVideos, entry]
			setReferenceVideos(nextVideos)
			syncDurationToReference(nextVideos)
		},
		[referenceVideos, syncDurationToReference],
	)

	const cancelVideoTrim = useCallback(() => {
		setVideoTrimUrl(null)
	}, [])

	const handleReferenceAudiosUpload = useCallback(
		async (e: React.ChangeEvent<HTMLInputElement>) => {
			const files = Array.from(e.target.files || [])
			if (!files.length) return
			const invalidFile = files.find((f) => validateAudioFile(f) !== null)
			if (invalidFile) {
				track('reference_audios', 'format', invalidFile.name.split('.').pop())
				setError(resolveValidationError('UNSUPPORTED_AUDIO'))
				if (e.target) e.target.value = ''
				return
			}
			// Форматы провайдера: Seedance читает только mp3 и wav — m4a/aac/ogg давали
			// «File type not supported» уже после списания. Говорим сразу.
			const wrongFormat = files.find((f) => !formatAllowed(f, audioFormats))
			if (wrongFormat && audioFormats?.length) {
				track('reference_audios', 'format', wrongFormat.name.split('.').pop())
				setError(`${modelName} принимает аудио только в ${formatsLabel(audioFormats)}. Файл «${wrongFormat.name}» не подойдёт.`)
				if (e.target) e.target.value = ''
				return
			}
			const maxRefs = selectedModel?.maxReferenceAudios ?? 3
			const remaining = maxRefs - referenceAudios.length
			if (remaining <= 0) {
				track('reference_audios', 'too_many', `${maxRefs}`)
				setError(`Максимум ${maxRefs} аудио.`)
				if (e.target) e.target.value = ''
				return
			}
			const tooBig = files.find((f) => f.size > audioMaxMb * 1024 * 1024)
			if (tooBig) {
				track('reference_audios', 'too_big', `${Math.round(tooBig.size / 1048576)} МБ`)
				setError(`Аудио превышает ${audioMaxMb} МБ.`)
				if (e.target) e.target.value = ''
				return
			}
			const filesToUpload = files.slice(0, remaining)
			const withDuration = await Promise.all(
				filesToUpload.map(async (f) => {
					const preview = URL.createObjectURL(f)
					const duration = await new Promise<number>((resolve) => {
						const audio = new Audio(preview)
						audio.addEventListener('loadedmetadata', () => resolve(audio.duration))
						audio.addEventListener('error', () => resolve(0))
					})
					return { file: f, preview, duration }
				}),
			)
			const currentTotal = totalSeconds(referenceAudios as (UploadedFile & { duration?: number })[])

			// Один файл длиннее потолка модели (Seedance 2.5 — 30 с, 2.0 — 15) — открываем тример.
			if (withDuration.length === 1 && withDuration[0].duration > audioMaxSeconds) {
				reportTrimOpened({ model: modelKey, field: 'reference_audios', detail: `${Math.round(withDuration[0].duration)} с` })
				URL.revokeObjectURL(withDuration[0].preview)
				setAudioTrimFile(withDuration[0].file)
				if (e.target) e.target.value = ''
				return
			}

			const newTotal = totalSeconds(withDuration)
			if (currentTotal + newTotal > audioMaxSeconds) {
				track('reference_audios', 'total_seconds', `${Math.round(currentTotal + newTotal)} с`)
				setError(`Суммарная длительность аудио не может превышать ${audioMaxSeconds} секунд`)
				withDuration.forEach((a) => URL.revokeObjectURL(a.preview))
				if (e.target) e.target.value = ''
				return
			}
			setReferenceAudios((prev) => [...prev, ...withDuration])
			setIsUploading(true)
			try {
				for (const a of withDuration) {
					const url = await uploadAndGetUrl(a.file, 'audio')
					setReferenceAudios((prev) =>
						prev.map((i) => (i.preview === a.preview ? { ...i, url } : i)),
					)
				}
			} catch (err) {
				setError(err instanceof Error ? err.message : t('generate.upload.error.uploadError'))
			} finally {
				setIsUploading(false)
				if (e.target) e.target.value = ''
			}
		},
		[selectedModel, referenceAudios, audioMaxSeconds, audioFormats, audioMaxMb, modelName, modelKey, track, setError, t, resolveValidationError],
	)

	const applyTrimmedAudio = useCallback(
		async (trimmedFile: File, duration: number) => {
			setAudioTrimFile(null)
			const preview = URL.createObjectURL(trimmedFile)
			const entry: UploadedFile & { duration: number } = { file: trimmedFile, preview, duration }
			setReferenceAudios((prev) => [...prev, entry])
			setIsUploading(true)
			try {
				const url = await uploadAndGetUrl(trimmedFile, 'audio')
				setReferenceAudios((prev) =>
					prev.map((i) => (i.preview === preview ? { ...i, url } : i)),
				)
			} catch (err) {
				setError(err instanceof Error ? err.message : t('generate.upload.error.uploadError'))
				setReferenceAudios((prev) => prev.filter((i) => i.preview !== preview))
				URL.revokeObjectURL(preview)
			} finally {
				setIsUploading(false)
			}
		},
		[setError, t],
	)

	const cancelAudioTrim = useCallback(() => {
		setAudioTrimFile(null)
	}, [])

	const dragProps = useCallback(
		(
			handler: (e: React.ChangeEvent<HTMLInputElement> | { target: { files: File[] } }) => void,
		) => ({
			onDragOver: (e: React.DragEvent) => {
				e.preventDefault()
				e.stopPropagation()
				setIsDragging(true)
			},
			onDragLeave: (e: React.DragEvent) => {
				e.preventDefault()
				e.stopPropagation()
				setIsDragging(false)
			},
			onDrop: (e: React.DragEvent) => {
				e.preventDefault()
				e.stopPropagation()
				setIsDragging(false)
				const file = e.dataTransfer.files?.[0]
				if (file) handler({ target: { files: [file] } })
			},
		}),
		[],
	)

	// «Что-то ещё грузится» по ФАКТУ (есть итем без .url), а не по флагу
	// isUploading: флаг один на все загрузки и сбрасывается finally первой
	// завершившейся — при параллельных загрузках он врёт (false, пока вторая
	// ещё в полёте). Потребители: disabled кнопок «Создать»
	// (GenerateInputBar) и send на главной (HomeTemp).
	const hasPendingUploads =
		(!!uploadedImage && !uploadedImage.url) ||
		(!!swapImage && !swapImage.url) ||
		(!!endImage && !endImage.url) ||
		(!!uploadedVideo && !uploadedVideo.url) ||
		(!!referenceVideo && !referenceVideo.url) ||
		referenceImages.some((i) => !i.url) ||
		omniReferenceImages.some((i) => !i.url) ||
		referenceVideos.some((v) => !v.url) ||
		referenceAudios.some((a) => !a.url)

	return {
		fileInputRef,
		swapInputRef,
		videoInputRef,
		endImageInputRef,
		refImagesInputRef,
		refVideoInputRef,
		refVideosInputRef,
		refAudiosInputRef,
		isUploading,
		setIsUploading,
		hasPendingUploads,
		isDragging,
		uploadedImage,
		setUploadedImage,
		referenceImages,
		setReferenceImages,
		slotImages,
		photoSlotsEngaged,
		handleSlotImageUpload,
		setSlotImagePending,
		removeSlotImage,
		setActiveSlotNums,
		swapImage,
		setSwapImage,
		uploadedVideo,
		setUploadedVideo,
		endImage,
		setEndImage,
		omniReferenceImages,
		setOmniReferenceImages,
		referenceVideo,
		setReferenceVideo,
		referenceVideos,
		setReferenceVideos,
		referenceAudios,
		setReferenceAudios,
		selectedVideoDuration,
		setSelectedVideoDuration,
		handleImageUpload,
		handleMultipleImageUpload,
		handleSwapImageUpload,
		handleVideoUpload,
		handleEndImageUpload,
		handleOmniRefImageUpload,
		handleReferenceVideoUpload,
		handleReferenceVideosUpload,
		handleReferenceAudiosUpload,
		audioTrimFile,
		applyTrimmedAudio,
		cancelAudioTrim,
		videoTrimUrl,
		applyTrimmedVideo,
		cancelVideoTrim,
		applyDefaultReferenceVideo,
		applyDefaultReferenceVideos,
		applyDefaultReferenceAudio,
		applyDefaultUploadedVideo,
		applyDefaultUploadedImage,
		dragProps,
		resetUploads,
		resetCardRefs,
		restoreInputs,
		uploadsEpoch,
	}
}
